<template>
    <div
        :id="id"
        class="tl-input-radio-group-wrapper"
    >
        <div
            class="tl-input-radio-group"
            :class="{ error: isError, disabled }"
            role="radiogroup"
        >
            <template
                v-for="item in items"
                :key="item.itemId"
            >
                <Tooltip
                    v-if="item.tooltip?.value"
                    :tip="item.tooltip?.value"
                    :placement="item.tooltip?.placement"
                >
                    <InputRadioSelector
                        v-model="modelValue"
                        :label="itemLabel ? itemLabel(item) : item.label"
                        :disabled="disabled"
                        :name="inputName"
                        :value="itemValue ? itemValue(item) : item.value"
                        :error="isError"
                    />
                </Tooltip>
                <InputRadioSelector
                    v-else
                    v-model="modelValue"
                    :label="itemLabel ? itemLabel(item) : item.label"
                    :disabled="disabled"
                    :name="inputName"
                    :value="itemValue ? itemValue(item) : item.value"
                    :error="isError"
                />
            </template>
        </div>

        <ErrorText
            v-if="error"
            :error="error"
            clip
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, useId } from 'vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import Tooltip from 'widget-components/atoms/Tooltip/Tooltip.vue'
import type { BlockPath, CPTooltip } from 'widget-components/integrations/CustomPage/types'
import InputRadioSelector from './InputRadioSelector.vue'

defineOptions({ inheritAttrs: false })

type InputRadioItem = {
    itemId?: string
    label: string
    value: string
    tooltip?: CPTooltip
    selectedItemId?: string
}

const props = defineProps<{
    id?: string
    items?: InputRadioItem[]
    initialValue?: string
    itemLabel?: (i: InputRadioItem) => string
    itemValue?: (i: InputRadioItem) => string
    error?: string
    disabled?: boolean
    blockPath?: BlockPath
    selectedItemId?: string
}>()

const inputName = computed(() => {
    return props.id || useId()
})

const modelValue = defineModel<string>({ default: '' })

onMounted(async () => {
    const preselectedItem = props.items?.find(i => i.itemId === props.selectedItemId)

    if (preselectedItem?.itemId) {
        // This is a hack for ListView, without it we can't see the default value, because selectItem fires too fast
        setTimeout(() => {
            modelValue.value = props.itemValue ? props.itemValue(preselectedItem) : preselectedItem.value
        }, 0)
    }
})

const isError = computed(() => !!props.error)
</script>

<style lang="sass" scoped>
@import './InputRadio.sass'
</style>
